import * as React from 'react';
import * as ReactDOM from 'react-dom';
import * as _ from 'underscore';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { imgUrl } from '../../common/utilities/ImgUrl';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { Keys } from '../../ts/helpers/Keys';
import { HeaderMenuItem } from './HeaderMenuItem';
import { HeaderStyles } from './HeaderStyles';
import { HelpMenuViewComponentProps } from './HelpMenu';

export class HelpMenuPanel extends React.Component<HelpMenuPanel.Props, HelpMenuPanel.State> {

  static displayName = 'HelpMenuPanel';

  static element = React.createFactory(HelpMenuPanel);

  private static helpMenuContainerStyle = _.extend({}, HeaderStyles.MenuPanelStyle, {
    height: 'auto'
  });

  private readonly boundItemSelected: () => void;
  private readonly boundShowAbout: () => void;
  private readonly boundShowGettingStarted: () => void;

  constructor(props: HelpMenuPanel.Props) {
    super(props);
    this.boundItemSelected = () => this.props.itemSelected();
    this.boundShowAbout = () => {
      this.props.helpActions.showAbout();
      this.props.itemSelected();
    };
    this.boundShowGettingStarted = () => {
      this.props.helpActions.showGettingStarted();
      this.props.itemSelected();
    };
  }

  public componentDidMount() {
    this.searchBoxNode().focus();
  }

  private searchBoxNode() {
    return $(ReactDOM.findDOMNode(this.refs['searchBox']));
  }

  public render(): React.DOMElement<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    return React.createElement('div', { style: HelpMenuPanel.helpMenuContainerStyle },
      this.renderSearchBox(),
      this.renderMenuItems()
    );
  }

  private renderSearchBox() {
    return React.createElement('input', {
      ref: 'searchBox',
      className: 'tb-placeholder',
      style: {
        width: '100%',
        boxSizing: 'border-box',
        height: CommonStyles.Sizes.RowHeight,
        fontFamily: CommonStyles.Fonts.HeaderFont,
        fontSize: CommonStyles.Sizes.NormalFontSize,
        border: '1px solid ' + CommonStyles.Colors.Grey178,
        color: CommonStyles.Colors.GreyTextColor,
        backgroundImage: imgUrl('magnifying_glass_small_normal.svg'),
        backgroundSize: '19px 19px',
        backgroundPosition: 'left center',
        backgroundRepeat: 'no-repeat',
        paddingTop: CommonStyles.Sizes.TextBoxPadding,
        paddingBottom: CommonStyles.Sizes.TextBoxPadding,
        paddingLeft: CommonStyles.Sizes.TextBoxPadding + CommonStyles.Sizes.SmallIconSize,
        outline: 'none'
      },
      placeholder: Messages.searchHelp(),
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === Keys.Enter) {
          const searchBox = this.searchBoxNode();
          const searchTerm = searchBox.val() as string;
          searchBox.val('');

          this.props.itemSelected();
          this.props.helpActions.searchHelp(searchTerm);
        }
      },'data-tb-test-id': 'help-menu-search-box'
    } as React.HTMLAttributes<HTMLInputElement>
    );
  }

  private renderMenuItems() {
    const menuItems: React.ReactElement<any>[] = [
      HeaderMenuItem.element({
        ref: 'server-help',
        key: 'help',
        label: this.props.serverHelpLabel,
        url: this.props.serverHelpUrl,
        target: '_blank',
        onClick: this.boundItemSelected,
        tbTestId: 'help-menu-server-help'
      })
    ];

    if (!this.props.isOfflineHelpEnabled) {
      menuItems.push(
        HeaderMenuItem.element({
          key: 'support',
          label: Messages.support(),
          url: this.props.supportUrl,
          target: '_blank',
          onClick: this.boundItemSelected,
          tbTestId: 'help-menu-support'
        }));
    }

    if (FeatureFlags.whatIsNewMenuOptionEnabled) {
        menuItems.push(
            HeaderMenuItem.element({
                key: 'whatIsNew',
                label: Messages.whatIsNew(),
                url: this.props.whatIsNewUrl,
                target: '_blank',
                onClick: this.boundItemSelected,
                tbTestId: 'what-is-new'
            }));
    }

    if (this.props.showGettingStartedLink) {
      menuItems.push(
        HeaderMenuItem.element({
          key: 'welcomeScreen',
          label: Messages.welcomeScreen(),
          onClick: this.boundShowGettingStarted,
          tbTestId: 'help-menu-welcome-screen'
        }));
    }

    menuItems.push(
      HeaderMenuItem.element({
        key: 'aboutServer',
        label: Messages.aboutServer({ SERVER_NAME: this.props.serverName }),
        onClick: this.boundShowAbout,
        tbTestId: 'help-menu-about-server'
      }));

    return menuItems;
  }

}

export module HelpMenuPanel {

  export interface Props extends HelpMenuViewComponentProps {
    itemSelected: () => void;
  }

  export interface State {
  }

}
